import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CLIENT_SUPPORT_HUB_PATH } from './supportHubPaths';
import { BM_BRAND_REQUESTS } from './brandManagerPaths';
import './brandmanager.css';
import './addbrand.css';
import BrandManagerShell from './components/BrandManagerShell';
import apiService from './services/api';
import {
  buildBrandManagerCards,
  clearLegacyMockManagedBrands,
  clearPendingAuthorizationBrands,
  searchPlatformBrands,
} from './lib/brandManagerStore';

function unwrapData(payload) {
  return payload?.data ?? payload;
}

const AddBrand = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [managedIds, setManagedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const refreshManagedIds = useCallback(async (catalogBrands) => {
    clearLegacyMockManagedBrands();
    clearPendingAuthorizationBrands();
    try {
      const mineRaw = await apiService.listMyBrands();
      const mine = unwrapData(mineRaw);
      const memberships = mine?.brandMemberships || [];
      const cards = buildBrandManagerCards({
        catalogBrands,
        memberships,
      });
      setManagedIds(new Set(cards.map((c) => c.id)));
    } catch {
      const cards = buildBrandManagerCards({ catalogBrands, memberships: [] });
      setManagedIds(new Set(cards.map((c) => c.id)));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      clearLegacyMockManagedBrands();
      try {
        const catalogRaw = await apiService.listBrandCatalog();
        const catalogData = unwrapData(catalogRaw);
        const brands = catalogData?.brands || [];
        if (cancelled) return;
        setCatalog(brands);
        await refreshManagedIds(brands);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.message || 'Failed to load brand catalog');
          setCatalog([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshManagedIds]);

  const results = useMemo(
    () => searchPlatformBrands(catalog, query, managedIds),
    [catalog, query, managedIds],
  );

  const hasQuery = useMemo(() => query.trim().length > 0, [query]);

  const handleAdd = async (brand) => {
    const brandId = brand.id || brand.brandId;
    if (!brandId) {
      setFeedback({ type: 'error', text: 'Invalid brand.' });
      return;
    }
    if (managedIds.has(brandId)) {
      setFeedback({
        type: 'info',
        text: `${brand.name} is already on your Brand Manager.`,
      });
      return;
    }
    try {
      setFeedback({ type: 'info', text: `Adding ${brand.name}…` });
      const raw = await apiService.joinBrand(brandId);
      const data = unwrapData(raw);
      const membership = data?.membership;
      setManagedIds((prev) => new Set([...prev, brandId]));
      await refreshManagedIds(catalog);
      const already = membership?.alreadyMember;
      setFeedback({
        type: 'success',
        text: already
          ? `${brand.name} is already on your Brand Manager.`
          : `${brand.name} was added with Limited access. Locations join this brand when you purchase a music subscription for them.`,
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err?.message || `Failed to add ${brand.name}`,
      });
    }
  };

  return (
    <BrandManagerShell>
      <div className="brand-manager-header">
        <h1>Add New Brand</h1>
        <p className="subtitle">
          Search the platform catalog and add a Brand to your Brand Manager
          portfolio
        </p>
      </div>

      <div className="add-brand-layout" data-testid="add-brand-page">
        <div className="add-brand-panel">
          <p className="add-brand-auth-notice">
            Adding a Brand enrolls it in your Client portfolio via canonical{' '}
            <strong>BrandMembership</strong> (
            <code>POST /me/brands/join</code>). New enrollments are{' '}
            <strong>Limited</strong> access. This never creates a second
            platform Brand when the catalog Brand already exists. Full access
            is granted by Super Admin only.
          </p>

          <label className="add-brand-search-label" htmlFor="brand-search">
            Search for brand name or industry…
          </label>
          <input
            id="brand-search"
            className="add-brand-search-input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for brand name or industry…"
            autoComplete="off"
            disabled={loading || Boolean(loadError)}
            data-testid="add-brand-search"
          />

          {feedback && (
            <div
              className={`add-brand-feedback add-brand-feedback--${feedback.type}`}
              role="status"
              data-testid="add-brand-feedback"
            >
              {feedback.text}
              {feedback.type === 'success' && (
                <button
                  type="button"
                  className="add-brand-feedback-link"
                  onClick={() => navigate('/brand-manager')}
                >
                  View Brand Manager
                </button>
              )}
            </div>
          )}

          <div className="add-brand-results">
            {loading ? (
              <div className="add-brand-empty">
                <p>Loading brands…</p>
              </div>
            ) : loadError ? (
              <div className="add-brand-empty">
                <p>{loadError}</p>
              </div>
            ) : results.length === 0 ? (
              <div className="add-brand-empty">
                <p>
                  {hasQuery
                    ? `No brands match “${query.trim()}”.`
                    : 'No brands available to add.'}
                </p>
              </div>
            ) : (
              <ul className="add-brand-list" data-testid="add-brand-results">
                {results.map((brand) => (
                  <li key={brand.id} className="add-brand-row">
                    <div className="add-brand-row-main">
                      <div className="add-brand-avatar">
                        {brand.logoUrl ? (
                          <img src={brand.logoUrl} alt="" />
                        ) : (
                          <span aria-hidden="true">🏢</span>
                        )}
                      </div>
                      <div>
                        <div className="add-brand-name">{brand.name}</div>
                        {brand.industry ? (
                          <div className="add-brand-meta">
                            {brand.industry}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {brand.alreadyAdded ? (
                      <span className="add-brand-added-badge">Added</span>
                    ) : (
                      <button
                        type="button"
                        className="add-brand-add-btn"
                        data-testid="add-brand-join-btn"
                        onClick={() => handleAdd(brand)}
                      >
                        Add to Brand Manager
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <aside
          className="add-brand-side"
          data-testid="add-brand-request-rail"
        >
          <div className="add-brand-side-card">
            <h2>Don&apos;t see your Brand?</h2>
            <p>
              If the Brand is not in the catalog yet, open Brand Requests.
              The formal request workflow is coming; Support remains a
              temporary escape hatch only.
            </p>
            <button
              type="button"
              className="add-brand-request-btn"
              data-testid="add-brand-request-cta"
              onClick={() => navigate(BM_BRAND_REQUESTS)}
            >
              Request a Brand
            </button>
            <button
              type="button"
              className="add-brand-contact-link"
              style={{ display: 'block', marginTop: 12 }}
              onClick={() =>
                navigate(CLIENT_SUPPORT_HUB_PATH, {
                  state: {
                    prefillMessage:
                      "I don't see my brand in the catalog. Please help me get it set up.",
                  },
                })
              }
            >
              Contact Support (temporary)
            </button>
          </div>
        </aside>
      </div>
    </BrandManagerShell>
  );
};

export default AddBrand;
